// License Keys tab: counters, search / state / platform filters, paginated
// key list with delete confirmation, and the "Import License Keys" dialog
// (paste or CSV file, column picker, platform).
import React, { useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { ArrowPathIcon, CloudArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline"
import { ImportLicenseKeys, DeleteLicenseKey } from "../../../AdminShared/api"
import { TextInput, TextArea, Select, errorMessage } from "../../../AdminShared/ui"
import { Chip, IconButton, Alert, Divider } from "../shared/shared"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const stateLabel = (s) => (s === "free" ? "Free" : s === "reserved" ? "Reserved" : "Taken")
const stateColor = (s) => (s === "free" ? "success" : s === "reserved" ? "warning" : "default")
const platformIdOf = (entry) => (typeof entry.platform === "object" ? entry.platform?._id : entry.platform)

export default function LicenseKeysTab({ editor, platforms, plk }) {
  const t = useProductEditorTexts()
  const { product, read } = editor
  const { data, loading, page, filter, setFilter, platformFilter, setPlatformFilter, search, setSearch, load } = plk
  const searchTimer = useRef(null)
  const csvInput = useRef(null)
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState("")
  const [csvColumn, setCsvColumn] = useState(null)
  const [csvHeaders, setCsvHeaders] = useState([])
  const [importPlatform, setImportPlatform] = useState("")
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const openImport = () => {
    setImportOpen(true)
    setImportText("")
    setImportResult(null)
    setCsvColumn(null)
    setCsvHeaders([])
    setImportPlatform("")
  }

  const onImportText = (text) => {
    setImportText(text)
    setImportResult(null)
    // Detect CSV: if first line has commas, parse headers
    const firstLine = text.split(/\r?\n/)[0] || ""
    if (!firstLine.includes(",")) {
      setCsvHeaders([])
      setCsvColumn(null)
      return
    }
    setCsvHeaders(firstLine.split(",").map((h, i) => ({ label: h.trim() || t("Column %s", i), value: i })))
    if (csvColumn === null) setCsvColumn(0)
  }

  const doImport = async () => {
    if (!importText.trim()) return
    setImporting(true)
    try {
      const res = await ImportLicenseKeys({ pid: product._id, text: importText, csvColumn: csvColumn ?? undefined, platformId: importPlatform || undefined })
      if (errorMessage(res)) return
      setImportResult(res)
      await load(1)
      editor.reload()
    } finally {
      setImporting(false)
    }
  }

  const doDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await DeleteLicenseKey(deleteTarget._id)
      setDeleteTarget(null)
      await load(page)
      editor.reload()
    } finally {
      setDeleting(false)
    }
  }

  const productPlatforms = (read("bookSettings.platforms", true) || []).map((entry) => platforms.find((p) => String(p._id) === String(platformIdOf(entry)))).filter(Boolean)
  // plkList populates the platform with its media key: take the absolute logo url from the platforms list
  const platformOf = (p) => platforms.find((x) => String(x._id) === String(p?._id)) || p
  const logo = (p) => platformOf(p)?.image && <img src={platformOf(p).image} alt="" className="h-4 w-auto object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <Typography component="h4" variant="admin_h4" className="my-0 inline-flex items-center gap-1.5">{t`License Keys`}<HelpHint helpKey="vendor.product.setup#license-keys" /></Typography>
        <Button variant="admin_primary" className="px-4" onClick={openImport} data-testid="plk-import-open">
          <CloudArrowUpIcon className="w-4 h-4" /> {t`Import Keys`}
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Chip color="success">{t("Free: %s", data?.freeCount ?? product?.stock ?? 0)}</Chip>
        <Chip color="warning">{t("Reserved: %s", data?.reservedCount ?? 0)}</Chip>
        <Chip>{t("Taken: %s", data?.activeCount ?? 0)}</Chip>
        <Chip color="primary">{t("Total: %s", data?.total ?? 0)}</Chip>
        <IconButton title={t`Refresh`} onClick={() => load(page)}><ArrowPathIcon className="w-4 h-4" /></IconButton>
      </div>

      <div className="flex items-center gap-3">
        <TextInput
          value={search}
          placeholder={t`Search keys...`}
          className="w-[260px]"
          onChange={(v) => {
            setSearch(v)
            clearTimeout(searchTimer.current)
            searchTimer.current = setTimeout(() => load(1, filter, v), 350)
          }}
        />
        <Select value={filter} onChange={(v) => { setFilter(v); load(1, v, search) }} placeholder={t`All`} options={[{ value: "free", label: t`Free` }, { value: "reserved", label: t`Reserved` }, { value: "active", label: t`Taken` }]} className="w-[140px]" searchEnabled={false} />
        <Select value={platformFilter} onChange={(v) => { setPlatformFilter(v); load(1, filter, search, v) }} placeholder={t`All Platforms`} options={platforms.map((p) => ({ value: p._id, label: p.name }))} className="w-[180px]" searchEnabled={false} />
      </div>

      {loading && <div className="h-1 bg-admin_primary/30 overflow-hidden rounded"><div className="h-full w-1/3 bg-admin_primary animate-pulse" /></div>}
      {!loading && data?.keys && (
        <div className="border border-admin_bordered rounded-md overflow-hidden">
          {data.keys.length === 0 && <p className="text-sm text-admin_text_secondary p-4 my-0">{t`No keys found.`}</p>}
          {data.keys.map((k, i) => (
            <div key={k._id}>
              {i > 0 && <Divider className="my-0" />}
              <div className="flex items-center gap-3 px-3 py-1.5" data-testid="plk-row">
                <span className="font-mono text-sm flex-1">{k.key}</span>
                {k.platform ? <span className="flex items-center gap-1 text-xs text-admin_text_secondary">{logo(k.platform)}{k.platform.name}</span> : <span className="text-xs text-admin_text_secondary/60">—</span>}
                <Chip color={stateColor(k.state)}>{t(stateLabel(k.state))}</Chip>
                <IconButton title={t`Delete`} className="hover:text-admin_error" onClick={() => setDeleteTarget(k)}><TrashIcon className="w-4 h-4" /></IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div className="flex items-center gap-2 text-sm">
          <Button variant="admin_text" disabled={page <= 1} onClick={() => load(page - 1)}>{t`Prev`}</Button>
          <span className="flex-1 text-center">{t("Page %s of %s", page, data.pages || 1)} &nbsp;·&nbsp; {t("%s keys", data.total ?? 0)}</span>
          <Button variant="admin_text" disabled={page >= (data.pages || 1)} onClick={() => load(page + 1)}>{t`Next`}</Button>
        </div>
      )}

      {importOpen && (
        <Modal isOpen onClose={() => setImportOpen(false)} variant="admin_compact" title={t`Import License Keys`} styleOverrides={{ container: "bg-admin_paper w-full max-w-[600px] max-h-[90vh] overflow-y-auto rounded-md border border-admin_bordered shadow-lg" }}>
          <div className="flex flex-col gap-3 p-4" data-testid="plk-import-dialog">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-admin_text_secondary my-0">{t`Paste keys below — one per line, or a CSV. Duplicate keys are skipped automatically.`} <HelpHint helpKey="vendor.product.setup#import-keys" /></p>
              <Button variant="admin_secondary" className="h-[32px] text-xs px-3 shrink-0" onClick={() => csvInput.current?.click()}><CloudArrowUpIcon className="w-4 h-4" /> {t`Select file`}</Button>
              <input
                ref={csvInput}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  const reader = new FileReader()
                  reader.onload = (ev) => onImportText(ev.target.result || "")
                  reader.readAsText(file)
                  e.target.value = ""
                }}
              />
            </div>
            <div>
              <span className="text-xs font-medium text-admin_text_secondary">{t`Platform`}</span>
              <Select value={importPlatform} onChange={setImportPlatform} placeholder={t`All Platforms`} options={productPlatforms.map((p) => ({ value: p._id, label: p.name }))} className="min-w-[220px]" searchEnabled={false} />
            </div>
            <TextArea rows={10} value={importText} onChange={onImportText} placeholder={"KEY-XXXX-YYYY\nKEY-XXXX-ZZZZ\n..."} className="font-mono text-xs" data-testid="plk-import-text" />
            {csvHeaders.length > 0 && (
              <div>
                <span className="text-xs font-medium text-admin_text_secondary">{t`CSV column that holds the keys`}</span>
                <Select value={csvColumn ?? 0} onChange={(v) => setCsvColumn(Number(v))} options={csvHeaders} className="min-w-[200px]" searchEnabled={false} />
              </div>
            )}
            {importing && <div className="h-1 bg-admin_primary/30 overflow-hidden rounded"><div className="h-full w-1/3 bg-admin_primary animate-pulse" /></div>}
            {importResult && <Alert severity="success">{t("Imported %s keys (%s duplicates skipped). Free keys now: %s", importResult.imported, importResult.skipped, importResult.freeCount)}</Alert>}
            <div className="flex justify-end gap-2">
              <Button variant="admin_secondary" onClick={() => setImportOpen(false)}>{t`Cancel`}</Button>
              <Button variant="admin_primary" onClick={doImport} disabled={importing || !importText.trim()} data-testid="plk-import-submit">{importing ? t`Importing…` : t`Import`}</Button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => !deleting && setDeleteTarget(null)} variant="admin_compact" title={t`Delete License Key`} styleOverrides={{ container: "bg-admin_paper w-full max-w-[420px] rounded-md border border-admin_bordered shadow-lg" }}>
          <div className="flex flex-col gap-3 p-4">
            <p className="text-sm text-admin_text_secondary my-0">{t`Are you sure you want to delete this key? This action cannot be undone.`}</p>
            <p className="font-mono text-sm p-2 bg-admin_neutral_1 rounded my-0">{deleteTarget.key}</p>
            <div className="flex justify-end gap-2">
              <Button variant="admin_secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>{t`Cancel`}</Button>
              <Button variant="admin_primary_red" onClick={doDelete} disabled={deleting}>{deleting ? t`Deleting…` : t`Delete`}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
